/* eslint-disable import/max-dependencies */
import React, { useEffect } from "react";
import { useSelector, useDispatch, useStore } from "react-redux";
import { IconButton } from "@mui/material";
import { useTable, useHandleRequest } from "@fas/cpa-state-manager/redux/hooks";
import type { Column } from "@fas/cpa-cabinet-ui/lib/Table/Table.types";
import type { Filters } from "@fas/cpa-state-manager/redux/actions/table/actions";
import {
  getTableFilters,
  getTableSorting,
  getTablePage,
  getTablePageSize,
  getTableFields,
  getTableData,
} from "@fas/cpa-state-manager/services/selectors/table";
import {
  changeTableItemsTotalAmount,
  changeTableFilters,
  changeTablePage,
  setTableData,
} from "@fas/cpa-state-manager/redux/actions/table";
import { getLoadingState } from "@fas/cpa-state-manager/services/selectors/loading";
import { addNotification, type Response } from "@fas/ui-framework";
import {
  PAYMENT_HISTORY_TABLE,
  PERIOD_STATE_TO_PAID,
  PERIOD_STATE_TO_STATUS_MAPPING,
  PERIOD_STATE_REFUND,
} from "../../constants";
import type { RequestBody } from "../../services/paymentHistoryApi";
import {
  fetchPaymentHistoryList,
  exportToPdf,
  exportToZip,
} from "../../services/paymentHistoryApi";
import {
  downloadPdf,
  downloadZip,
  formatCurrency,
  getFileNameFromHeaders,
  isEqualPreviousBuilder,
  readObjFromFile,
} from "../../utils";
import type { PaymentHistoryData } from "../../components/PaymentHistory";
import Download from "../../icons/download";

export type PaymentHistoryColumnData = PaymentHistoryData & {
  invoice: any;
};
export type UsePaymentHistoryTable = ReturnType<
  typeof useTable
> & {
  columns: Column<PaymentHistoryColumnData>[];
};
export type UsePaymentHistoryActions = {
  classes?: any;
  filters: Filters;
  isLoading: boolean;
  isExportLoading: boolean;
  isNoData: boolean;
  onApply: () => unknown;
  onExport: () => unknown;
  onChangeDate: (arg0: { startDate: string; endDate: string }) => unknown;
};
const isEqualPrevious = isEqualPreviousBuilder();
// eslint-disable-next-line max-len
export const getColumns = (
  onExport: (id: number) => unknown,
  isLoadingExport: boolean
): Column<PaymentHistoryColumnData>[] => [
  {
    field: "created",
    label: "Created",
    sortable: true,
  },
  {
    field: "dateFrom",
    label: "Period from",
    sortable: true,
  },
  {
    field: "dateTo",
    label: "Period to",
    sortable: true,
  },
  {
    field: "paidTotal",
    label: "Paid total",
    sortable: true,
    render: ({ currency, paidTotal }) => (
      <span key={`${currency}-${paidTotal}`}>
        {formatCurrency(currency, paidTotal)}
      </span>
    ),
  },
  {
    field: "paidAt",
    label: "Paid on",
    sortable: true,
  },
  {
    field: "isGroupInvoice",
    label: "Group invoice",
    sortable: false,
    render: ({ isGroupInvoice }) => (
      <span>
        {isGroupInvoice ? "Yes" : "No"}
      </span>
    ),
  },
  {
    field: "stateId",
    label: "Status",
    sortable: true,
    render: ({ stateId }) => (
      <span>
        {PERIOD_STATE_TO_STATUS_MAPPING[stateId]}
      </span>
    ),
  },
  {
    field: "invoice",
    label: "Invoice",
    render: ({ id, stateId, isDownloadable }: PaymentHistoryData) =>
      (isDownloadable === true ||
        (isDownloadable === undefined &&
          (stateId === PERIOD_STATE_TO_PAID ||
            stateId === PERIOD_STATE_REFUND))) && (
        <IconButton
          disabled={isLoadingExport}
          sx={(theme: any): any => ({
            margin: -1,
            [theme.breakpoints.down("lg")]: {
              "& > svg": {
                fontSize: "1rem",
              },
            },
          })}
          size="small"
          onClick={() => {
            onExport(id);
          }}
        >
          <Download />
        </IconButton>
      ),
  },
];

export const usePaymentHistoryTable: () => UsePaymentHistoryTable = () => {
  const dispatch = useDispatch();
  const baseProps = useTable(PAYMENT_HISTORY_TABLE);
  const fields = useSelector((state) =>
    getTableFields(state, PAYMENT_HISTORY_TABLE)
  );
  const isLoadingExport = useSelector((state) =>
    getLoadingState(state, `${PAYMENT_HISTORY_TABLE}-export`)
  );
  const {
    changeLoading: changeExportLoading,
    onFail: onExportFail,
    onFinally: onExportFinally,
  } = useHandleRequest(`${PAYMENT_HISTORY_TABLE}-export`);

  const onExportSingle = async (id: number) => {
    changeExportLoading(true);
    exportToPdf(id)
      .then(({ data, headers }) => {
        const name = getFileNameFromHeaders(headers);
        downloadPdf(data, name);
      })
      .catch(async (error) => {
        if (error.response.data instanceof Blob) {
          const { message } = (await readObjFromFile(error.response.data)) as {
            message: string;
          };
          dispatch(
            addNotification({
              message,
              severity: "error",
            })
          );
        }
        else {
          onExportFail(error);
        }
      })
      .finally(onExportFinally);
  };

  const columns: Column<PaymentHistoryColumnData>[] = getColumns(
    onExportSingle,
    isLoadingExport
  ).filter(({ field }) => [...fields, "invoice"].includes(field));
  const isLoadingTable = baseProps.isLoading || isLoadingExport;
  return {
    ...baseProps,
    columns,
    isLoading: isLoadingTable,
    data: baseProps.isLoading ? [] : baseProps.data,
  };
};

function getPaymentHistoryRequest(store: { getState: () => any }) {
  const state = store.getState();
  const filters = getTableFilters(state, PAYMENT_HISTORY_TABLE);
  const tableSorting = getTableSorting(state, PAYMENT_HISTORY_TABLE);
  const page = getTablePage(state, PAYMENT_HISTORY_TABLE);
  const limit = getTablePageSize(state, PAYMENT_HISTORY_TABLE);
  const fields = getTableFields(state, PAYMENT_HISTORY_TABLE);
  const [current = "created", direction = "desc"] =
    Object.entries(tableSorting)[0] || [];
  const { dateFrom: from, dateTo: to } = filters;
  const requestBody: RequestBody = {
    filters: {
      created: [
        {
          from,
          to,
        },
      ],
    },
    sorting: {
      current,
      direction,
    },
    fields,
    page,
    limit,
  };
  return requestBody;
}

export const usePaymentHistoryActions: () => UsePaymentHistoryActions = () => {
  const store = useStore();
  const dispatch = useDispatch();
  const { isLoading, changeLoading, onFail, onFinally } = useHandleRequest(
    PAYMENT_HISTORY_TABLE
  );
  const {
    isLoading: isExportLoading,
    changeLoading: changeExportLoading,
    onFail: onExportFail,
    onFinally: onExportFinally,
  } = useHandleRequest(`${PAYMENT_HISTORY_TABLE}-export`);
  const filters = useSelector((state) =>
    getTableFilters(state, PAYMENT_HISTORY_TABLE)
  );
  const tableSorting = useSelector((state) =>
    getTableSorting(state, PAYMENT_HISTORY_TABLE)
  );
  const page = useSelector((state) =>
    getTablePage(state, PAYMENT_HISTORY_TABLE)
  );
  const limit = useSelector((state) =>
    getTablePageSize(state, PAYMENT_HISTORY_TABLE)
  );
  const tableData = useSelector(
    (state) =>
      getTableData(state, PAYMENT_HISTORY_TABLE) as PaymentHistoryData[]
  );
  useEffect(() => {
    onApply();
  }, [tableSorting, page, limit]);

  const onApply = () => {
    const requestBody = getPaymentHistoryRequest(store);

    if (!isEqualPrevious(requestBody.filters) && requestBody.page !== 1) {
      dispatch(changeTablePage(PAYMENT_HISTORY_TABLE, 1));
      return;
    }

    changeLoading(true);
    fetchPaymentHistoryList(requestBody)
      .then(({ data, count }) => {
        dispatch(setTableData(PAYMENT_HISTORY_TABLE, data));
        dispatch(changeTableItemsTotalAmount(PAYMENT_HISTORY_TABLE, count));
      })
      .catch(onFail)
      .finally(onFinally);
  };

  const onExport = async () => {
    changeExportLoading(true);
    const requestBody = getPaymentHistoryRequest(store);
    exportToZip(requestBody)
      .then(({ data, headers }) => {
        const name: string = getFileNameFromHeaders(headers);
        downloadZip(data, name);
      })
      .catch(async (error: any) => {
        if (error.response.data instanceof Blob) {
          const { message } = (await readObjFromFile(error.response.data)) as {
            message: string;
          };
          dispatch(
            addNotification({
              message,
              severity: "error",
            })
          );
        }
        else {
          onExportFail(error);
        }
      })
      .finally(onExportFinally);
  };

  const onChangeDate = ({
    startDate,
    endDate,
  }: {
    startDate: string;
    endDate: string;
  }) => {
    if (filters.dateFrom !== startDate || filters.dateTo !== endDate) {
      dispatch(
        changeTableFilters(PAYMENT_HISTORY_TABLE, {
          ...filters,
          dateFrom: startDate,
          dateTo: endDate,
        } as unknown as Filters)
      );
      onApply();
    }
  };

  return {
    filters,
    isLoading,
    isExportLoading,
    isNoData: tableData.length === 0,
    onChangeDate,
    onApply,
    onExport,
  };
};
